import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { ControlPogResetPlanogramProduct } from '../../../types';
import { ImageZoomModal } from '../../embeddings/components/ImageZoomModal';

interface PlanogramProductDialogProps {
  product: ControlPogResetPlanogramProduct | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  positionLabel?: string;
}

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-right">{value ?? '—'}</span>
  </div>
);

export const PlanogramProductDialog: React.FC<PlanogramProductDialogProps> = ({
  product,
  onClose,
  onPrev,
  onNext,
  positionLabel,
}) => {
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);

  useEffect(() => {
    if (!product) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [product, onPrev, onNext, onClose]);

  if (!product) return null;

  const facings =
    product.horizontalFacings || product.verticalFacings || product.depthFacings
      ? `${product.horizontalFacings ?? 1} × ${product.verticalFacings ?? 1} × ${
          product.depthFacings ?? 1
        }`
      : null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 1500 }}
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev?.();
        }}
        disabled={!onPrev}
        className="p-2 mr-2 text-white bg-black bg-opacity-40 rounded-full hover:bg-opacity-60 disabled:opacity-20 disabled:cursor-default"
        title="Previous product (←)"
      >
        <ChevronLeftIcon />
      </button>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="pr-4">
            <h3 className="text-lg font-semibold">{product.productName ?? 'Unknown product'}</h3>
            {positionLabel && <div className="text-xs text-gray-400 mt-0.5">{positionLabel}</div>}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="p-5 flex gap-5">
          {product.imageUri && (
            <img
              src={product.imageUri}
              alt={product.productName ?? 'product'}
              className="w-32 h-32 object-contain border rounded-md cursor-zoom-in flex-shrink-0"
              onClick={() => setIsImageZoomOpen(true)}
            />
          )}
          <div className="flex-1 min-w-0">
            <DetailRow label="UPC" value={<span className="font-mono">{product.upc}</span>} />
            <DetailRow
              label="Item Number"
              value={<span className="font-mono">{product.itemNumber}</span>}
            />
            <DetailRow
              label="Position"
              value={
                product.shelfNumber != null
                  ? `Shelf ${product.shelfNumber}, position ${product.positionNumber ?? '?'}`
                  : null
              }
            />
            <DetailRow label="Facings (H × V × D)" value={facings} />
            <DetailRow label="Price" value={product.price != null ? `£${product.price}` : null} />
            <DetailRow label="Case Pack" value={product.casePack} />
            <DetailRow label="Inventory Level" value={product.inventoryLevel} />
          </div>
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext?.();
        }}
        disabled={!onNext}
        className="p-2 ml-2 text-white bg-black bg-opacity-40 rounded-full hover:bg-opacity-60 disabled:opacity-20 disabled:cursor-default"
        title="Next product (→)"
      >
        <ChevronRightIcon />
      </button>
      {product.imageUri && (
        // Stop zoom-modal clicks from bubbling to the overlay's onClose (which
        // would close the product dialog behind the zoom view).
        <div onClick={(e) => e.stopPropagation()}>
          <ImageZoomModal
            isOpen={isImageZoomOpen}
            imageSrc={product.imageUri}
            onClose={() => setIsImageZoomOpen(false)}
          />
        </div>
      )}
    </div>
  );
};
